<script setup>
import {onMounted, watch} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import AssistantChat from './AssistantChat.vue';
import {useAssistantPanel} from '~/composables/useAssistantPanel';

// A docked, non-modal help popup (mounted once in app.vue for signed-in users).
// Unlike a v-dialog it has no scrim and doesn't lock the route, so the user can
// keep reading an answer while clicking around the app. Open state lives in
// useAssistantPanel (survives navigation); the /help page keeps its own
// embedded card, and both render the same shared conversation via useAssistant.
const {isOpen, close} = useAssistantPanel();
const route = useRoute();
const router = useRouter();

// Deep-link support: ?assistant=open opens the panel, then we strip the param
// so the panel's lifetime is owned by the flag, not the URL.
onMounted(() => {
  if (route.query.assistant === 'open') {
    isOpen.value = true;
    const {assistant, ...rest} = route.query;
    router.replace({query: rest});
  }
});
watch(() => route.query.assistant, (val) => {
  if (val === 'open') {
    isOpen.value = true;
    const {assistant, ...rest} = route.query;
    router.replace({query: rest});
  }
});
</script>

<template>
  <Teleport to="body">
    <Transition name="assistant-panel">
      <div v-if="isOpen" class="assistant-panel" role="dialog" aria-label="Tap Leagues assistant">
        <div class="assistant-panel-head">
          <div class="assistant-panel-title">
            <v-icon size="20" color="primary">mdi-robot-happy-outline</v-icon>
            <span>Tap Leagues assistant</span>
            <v-chip size="x-small" color="primary" variant="tonal" class="ml-1">Beta</v-chip>
          </div>
          <button type="button" class="assistant-panel-close" aria-label="Close assistant" @click="close">
            <v-icon size="20">mdi-close</v-icon>
          </button>
        </div>
        <assistant-chat hide-header class="assistant-panel-body"/>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
.assistant-panel {
  position: fixed;
  z-index: 2500; // above the app bar and the messaging overlay
  right: 20px;
  bottom: 20px;
  width: 380px;
  max-width: calc(100vw - 40px);
  height: 560px;
  max-height: calc(100vh - 40px);
  display: flex;
  flex-direction: column;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
  overflow: hidden;

  // Phone widths: dock to a near-full-screen bottom sheet, still non-modal.
  @media (max-width: 600px) {
    bottom: 0;
    right: 0;
    left: 0;
    width: 100vw;
    max-width: 100vw;
    height: 82vh;
    border-radius: 16px 16px 0 0;
  }
}

.assistant-panel-head {
  flex-shrink: 0;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 8px 0 16px;
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));
}

.assistant-panel-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 800;
  font-size: 14px;
}

.assistant-panel-close {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: transparent;

  &:hover {
    background: rgba(var(--v-theme-borderLight), 0.5);
  }
}

.assistant-panel-body {
  flex: 1;
  min-height: 0; // let the inner message list scroll instead of the panel
}

// Slide/fade in from the corner
.assistant-panel-enter-active,
.assistant-panel-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.assistant-panel-enter-from,
.assistant-panel-leave-to {
  opacity: 0;
  transform: translateY(12px);
}
</style>
